import { Injectable } from "@angular/core";

import { Action, Selector, State, StateContext, Store } from "@ngxs/store";
import { tap } from "rxjs";
import { EntityService, HelpService, LogService, SubscriptionService, TagService } from "../services/api";
import
{
    ChangeLanguage,
    ChangeProductView,
    FilterLogsText,
    FilterLogsType,
    GetTagNames,
    LoadAllLanguages,
    LoadApiVersion,
    LoadLanguages,
    LoadLogs,
    LoadLogsOverview,
    LoadNoteTypes,
    LoadProductTypes,
    LoadTags,
    LoadUserSubscriptions,
    LoadVersionTypes,
    LoadVisibilities
} from "./app.actions";
import { defaultAppState, IAppState } from "./app.definitions";

@State<IAppState>({
    name: 'app',
    defaults: defaultAppState
})
@Injectable()
export class AppState
{
    constructor(private entityService: EntityService,
        private helpService: HelpService,
        private subscriptionService: SubscriptionService,
        private logService: LogService,
        private tagService: TagService,
        private store: Store)
    {
    }

    @Selector([AppState])
    public static visibilities(state: IAppState)
    {
        return state.visibilities;
    }

    @Selector([AppState])
    public static tags(state: IAppState)
    {
        return state.tags;
    }

    @Selector([AppState])
    public static tagNames(state: IAppState)
    {
        return state.tagNames;
    }


    @Selector([AppState])
    public static languages(state: IAppState)
    {
        return state.languages;
    }

    @Selector([AppState])
    public static allLanguages(state: IAppState)
    {
        return state.allLanguages;
    }

    @Selector([AppState])
    public static selectedLanguage(state: IAppState)
    {
        return state.selectedLanguage
    }

    @Selector([AppState])
    public static noteTypes(state: IAppState)
    {
        return state.noteTypes;
    }

    @Selector([AppState])
    public static productTypes(state: IAppState)
    {
        return state.productTypes;
    }

    @Selector([AppState])
    public static versionTypes(state: IAppState)
    {
        return state.versionTypes;
    }

    @Selector([AppState])
    public static productView(state: IAppState)
    {
        return state.productView;
    }

    @Selector([AppState])
    public static apiVersion(state: IAppState)
    {
        return state.apiVersion;
    }

    @Selector([AppState])
    public static userSubscriptions(state: IAppState)
    {
        return state.userSubscriptions;
    }

    @Selector([AppState])
    public static logsOverview(state: IAppState)
    {
        return state.logsOverview;
    }

    @Selector([AppState])
    static filteredLogs(state: IAppState)
    {
        let logs = state.logs;
        if (logs)
        {
            if (state.filteredLogsType)
                logs = logs.filter(l => !l.path?.toLowerCase().includes('/api/ast/') &&
                    !l.path?.toLowerCase().includes('/api/artefactmanagement/'))
            if (state.filteredLogsText.length > 0)
                logs = logs.filter(l => l.userMail?.toLowerCase().includes(state.filteredLogsText.toLowerCase()) || l.path?.toLowerCase().includes(state.filteredLogsText.toLowerCase()));
        }

        return logs;
    }

    //GET
    @Action(LoadLogs)
    public loadLogs({ patchState }: StateContext<IAppState>, action: LoadLogs)
    {
        if (action.startDate && action.endDate)
            return this.logService.getLogs(action.startDate, action.endDate).pipe(tap(logs => patchState({ logs })))
        else
            return this.logService.getLast2WeeksLogs().pipe(tap(logs => patchState({ logs })))
    }

    @Action(LoadLogsOverview)
    public loadLogsOverview({ patchState }: StateContext<IAppState>, action: LoadLogsOverview)
    {
        return this.logService.getAccessOverview().pipe(tap(logsOverview => patchState({ logsOverview })))
    }

    @Action(LoadUserSubscriptions)
    public loadUserSubscriptions({ patchState }: StateContext<IAppState>, action: LoadUserSubscriptions)
    {
        return this.subscriptionService.getSubscriptionsOfUser().pipe(
            tap(userSubscriptions => patchState({ userSubscriptions }))
        )
    }

    @Action(LoadVisibilities)
    public loadVisibilities(ctx: StateContext<IAppState>)
    {
        if (ctx.getState().visibilities.length === 0)
        {
            return this.entityService.getVisibilities().pipe(
                tap(
                    visibilities =>
                    {
                        ctx.patchState({ visibilities });
                    }
                )
            );
        }
        return;
    }

    @Action(LoadTags)
    public loadTags(ctx: StateContext<IAppState>, action: LoadTags)
    {
        return this.tagService.getTags().pipe(
            tap(
                tags =>
                {
                    // Patch the state with both tags and tagNames
                    ctx.patchState({ tags });
                    // Dispatch the GetTagNames action
                    this.store.dispatch(new GetTagNames());
                }
            )
        );
    }

    @Action(GetTagNames)
    public getTagNames(ctx: StateContext<IAppState>, action: GetTagNames)
    {
        // If tags are already loaded, use them to update tagNames
        if (ctx.getState().tags.length > 0)
        {
            const tags = ctx.getState().tags;
            const tagNames = tags.map(tag => tag.name).filter((name): name is string => name !== undefined);
            ctx.patchState({ tagNames });
            return;
        }
        return;
    }

    @Action(LoadAllLanguages)
    public loadAllLanguages(ctx: StateContext<IAppState>, action: LoadAllLanguages)
    {
        if (ctx.getState().allLanguages.length === 0)
        {
            return this.entityService.getAllLanguages().pipe(
                tap(
                    allLanguages =>
                    {
                        ctx.patchState({ allLanguages })
                    }
                )
            );
        }
        return;
    }

    @Action(LoadLanguages)
    public loadLanguages(ctx: StateContext<IAppState>, action: LoadLanguages)
    {
        if (ctx.getState().languages.length === 0)
        {
            return this.entityService.getLanguages().pipe(
                tap(
                    languages =>
                    {
                        ctx.patchState({ languages })
                    }
                )
            );
        }
        return;
    }

    @Action(LoadApiVersion)
    public loadApiVersion(ctx: StateContext<IAppState>, action: LoadLanguages)
    {
        if (ctx.getState().apiVersion.length === 0)
        {
            return this.helpService.getApiVersion().pipe(
                tap(
                    apiVersion =>
                    {
                        ctx.patchState({ apiVersion })
                    }
                )
            );
        }
        return;
    }

    @Action(LoadProductTypes)
    public LoadProductTypes(ctx: StateContext<IAppState>, action: LoadProductTypes)
    {
        if (ctx.getState().productTypes.length === 0)
        {
            return this.entityService.getProductTypes().pipe(
                tap(
                    productTypes =>
                    {
                        ctx.patchState({ productTypes })
                    }
                )
            );
        }
        return;
    }

    @Action(LoadVersionTypes)
    public LoadVersionTypes(ctx: StateContext<IAppState>, action: LoadVersionTypes)
    {
        if (ctx.getState().versionTypes.length === 0)
        {
            return this.entityService.getVersionTypes().pipe(
                tap(
                    versionTypes =>
                    {
                        ctx.patchState({ versionTypes })
                    }
                )
            );
        }
        return;
    }

    @Action(LoadNoteTypes)
    public LoadNoteTypes(ctx: StateContext<IAppState>, action: LoadNoteTypes)
    {
        if (ctx.getState().noteTypes.length === 0)
        {
            return this.entityService.getNoteTypes().pipe(
                tap(
                    noteTypes =>
                    {
                        ctx.patchState({ noteTypes })
                    }
                )
            );
        }
        return;
    }

    //OTHER
    @Action(FilterLogsText)
    filterLogsText({ patchState }: StateContext<IAppState>, action: FilterLogsText)
    {
        patchState({ filteredLogsText: action.filterValue });
    }

    @Action(FilterLogsType)
    filterLogsType({ patchState }: StateContext<IAppState>, action: FilterLogsType)
    {
        patchState({ filteredLogsType: action.filter });
    }

    @Action(ChangeLanguage)
    public changeLanguage({ patchState }: StateContext<IAppState>, action: ChangeLanguage)
    {
        patchState({ selectedLanguage: action.lang });
    }

    @Action(ChangeProductView)
    public changeProductView({ patchState }: StateContext<IAppState>, action: ChangeProductView)
    {
        patchState({ productView: action.view });
    }
}
